import axios from 'axios'
import {useMutation, UseMutationResult, useQuery, UseQueryResult} from '@tanstack/react-query'
import {guid, redirectToStore} from 'src/service/utils'
import { RequestData, ResponseHeader } from 'src/types/type'
import type { CMD_TYPES } from 'src/types/cmdtype'
import { ERR_CODES } from 'src/types/errcode'
import { captureServerError, captureUnexpectedErrCode } from 'src/service/utils/sentry/api'
import { addApiBreadcrumb } from 'src/service/utils/sentry/breadcrumb'

const requestID = guid()

const useAPI = (cmdType: keyof typeof CMD_TYPES): any => {
    const cmdTypeList: typeof CMD_TYPES = {
        requestTermsList: 10020,
        requestJoin: 10014,
        requestPartnerList: 10015,
        requestSendCertNumber: 10013,
        requestTermsDetail: 10021,
        CheckMvnoMember: 10016,
        CheckVulnerable: 10018,
    }
    const getServerURL = (): string => {
        const host = window.location.host
        return host.includes('localhost') || host.includes('dev-') || host.includes('5.0')
            ? 'https://dev-wcrs.familycare.ai/webjoin'
            : 'https:///n-wcrs.familycare.ai/webjoin'
    }

    const createHttpData: any = (
        cmdType: keyof typeof CMD_TYPES,
        requestBody: unknown,
    ): RequestData<unknown> => {
        return {
            Header: {
                CmdType: cmdTypeList[cmdType],
                RequestID: requestID,
            },
            Body: requestBody,
        }
    }
    const requestWCRS = async <T>(CmdType: keyof typeof CMD_TYPES, ReqBody?: T): Promise<any> => {
        const CMD_TYPE = cmdTypeList[CmdType]
        addApiBreadcrumb('request', { cmdType: CMD_TYPE })
        return await axios
            .post(getServerURL(), createHttpData(CmdType, ReqBody), {withCredentials: true})
            .then(async (response) => {
                const {Header, Body} = response?.data
                if (Header && Header.ErrCode !== ERR_CODES.Success) {
                  addApiBreadcrumb('response', {
                    cmdType: CMD_TYPE,
                    errCode: Header?.ErrCode,
                    errMsg: Header?.ErrMsg,
                  })
                  captureUnexpectedErrCode(Header)
                }

                return await handleError(Header, Body)
            })
            .catch((error) => {
                captureServerError(error, CMD_TYPE)
                throw error
            })
    }
    const handleError = async (Header: ResponseHeader, Body: any): Promise<any> => {
      const STYLED_LANDING_PAGES  = [/\/mkt04/i, /\/mkt05/i]
      const isStyledLandingPage = STYLED_LANDING_PAGES.some((r) => r.test(window.location.pathname))
      if (Header?.ErrCode === 0) {
          return {Header, Body}
      }
      if (!isStyledLandingPage) {
            switch (Header?.ErrCode) {
                case 1008:
                    alert(Header?.ErrMsg)
                    window.dataLayer = window.dataLayer || []
                    window.dataLayer.push({event: 'already_signup'})
                    redirectToStore()
                    break
                case 1006:
                case 1021:
                case 1031:
                case 1033:
                case 1036:
                case 1040:
                case 9000:
                case 9001:
                    alert(Header?.ErrMsg)
                    window.location.reload()
                    break
                default:
                    alert(
                        Header?.ErrMsg ??
                        `(${Header?.CmdType}) ${Header?.ErrCode} : 알 수 없는 오류가 발생했습니다.`,
                    )
                    break
            }
        }
        return {Header, Body}
    }

    // react-query
    const useSetQuery = <T, E = unknown>(data: unknown): UseQueryResult<T | undefined, E> => {
        return useQuery([cmdType, data ?? {}], async () => await requestWCRS(cmdType, data))
    }
    type UseSetMutationReturnType<T> = UseMutationResult<T, Error, unknown>

    const useSetMutation = <T>(data: unknown): UseSetMutationReturnType<T> => {
        return useMutation([cmdType, data], async () => await requestWCRS(cmdType, data))
    }
    return {
        useSetQuery,
        useSetMutation,
    }
}

export default useAPI
